import {
  DescriptionEntry,
  NumberFieldEntry,
  isNumberFieldEntryEdited,
  TextFieldEntry,
  isTextFieldEntryEdited,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import { is } from "bpmn-js/lib/util/ModelUtil";

const LOW_PRIORITY = 500;

/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function PublishPropertiesProvider(
  propertiesPanel,
  translate,
  moddle
) {
  /**
   * Return the groups provided for the given element.
   *
   * @param {DiagramElement} element
   *
   * @return {(Object[]) => (Object[])} groups middleware
   */
  this.getGroups = function (element) {
    /**
     * We return a middleware that modifies
     * the existing groups.
     *
     * @param {Object[]} groups
     *
     * @return {Object[]} modified groups
     */
    return function (groups) {
      if (is(element, "bpmn:Process")) {
        groups.push(createGroup(element, translate));
      }

      return groups;
    };
  };

  // Register our custom properties provider.
  // Use a lower priority to ensure it is loaded after
  // the basic BPMN properties.
  propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

function createGroup(element, translate) {
  return {
    id: "publish-group",
    label: translate("Publish"),
    entries: [
      {
        id: "description-header",
        forId: "publish-group",
        value: (
          <div>
            <p>
              Publish the current process model. Brixx-Process-Engine runs under{" "}
              <button
                class="button-properties"
                onClick={checkServerHeartbeat}
              >
                {serverURL}
              </button>
            </p>
          </div>
        ),
        component: ProcessDescription,
      },
      {
        id: "version",
        component: Version,
        isEdited: isNumberFieldEntryEdited,
      },
      {
        id: "edition",
        component: Edition,
        isEdited: isTextFieldEntryEdited,
      },
      {
        id: "url",
        component: URL,
        isEdited: isTextFieldEntryEdited,
      },
      {
        id: "user",
        component: User,
        isEdited: isTextFieldEntryEdited,
      },
      {
        id: "password",
        component: Password,
        isEdited: isTextFieldEntryEdited,
      },
      {
        component: Open,
      },
      {
        component: Download,
      },
      {
        component: PublishModel,
      },
    ],
  };
}

function Version(props) {
  const { element, id } = props;

  const modeling = useService("modeling");
  const translate = useService("translate");
  const debounce = useService("debounceInput");

  const getValue = () => {
    return element.businessObject.version || "";
  };

  const setValue = (value) => {
    return modeling.updateProperties(element, {
      version: value,
    });
  };

  const entry = (
    <NumberFieldEntry
      id={id}
      element={element}
      label={translate("Process Model version")}
      description={translate("An optional model version number.")}
      getValue={getValue}
      setValue={setValue}
      debounce={debounce}
      disabled={tryoutButtonsDisabled}
    />
  );

  return entry;
}

function Edition(props) {
  const { element, id } = props;

  const modeling = useService("modeling");
  const translate = useService("translate");
  const debounce = useService("debounceInput");

  const getValue = () => {
    return element.businessObject.engine || "";
  };

  const setValue = (value) => {
    return modeling.updateProperties(element, {
      engine: value,
    });
  };

  const entry = (
    <TextFieldEntry
      id={id}
      element={element}
      label={translate("Process Engine identifier")}
      description={translate("An optional engine identifier.")}
      getValue={getValue}
      setValue={setValue}
      debounce={debounce}
      disabled={tryoutButtonsDisabled}
    />
  );

  return entry;
}

function URL(props) {
  const { element, id } = props;

  const modeling = useService("modeling");
  const translate = useService("translate");
  const debounce = useService("debounceInput");

  const getValue = () => {
    return element.businessObject.url || "";
  };

  const setValue = (value) => {
    serverURL = value;
    modeling.updateProperties(element, {
      url: value,
    });
  };

  const entry = (
    <TextFieldEntry
      id={id}
      element={element}
      label={translate("Process Engine URL")}
      getValue={getValue}
      setValue={setValue}
      debounce={debounce}
      disabled={tryoutButtonsDisabled}
    />
  );

  return entry;
}

function User(props) {
  const { element, id } = props;

  const translate = useService("translate");
  const debounce = useService("debounceInput");

  const getValue = () => {
    return credentials.user;
  };

  const setValue = (value) => {
    credentials.user = value;
  };

  const entry = (
    <TextFieldEntry
      id={id}
      element={element}
      label={translate("Username")}
      getValue={getValue}
      setValue={setValue}
      debounce={debounce}
      disabled={tryoutButtonsDisabled}
    />
  );

  return entry;
}

function Password(props) {
  const { element, id } = props;

  const translate = useService("translate");
  const debounce = useService("debounceInput");

  const getValue = () => {
    return credentials.password;
  };

  const setValue = (value) => {
    credentials.password = value;
  };

  const entry = (
    <TextFieldEntry
      id={id}
      element={element}
      label={translate("Password")}
      getValue={getValue}
      setValue={setValue}
      debounce={debounce}
      disabled={tryoutButtonsDisabled}
    />
  );

  return entry;
}

function ProcessDescription(props) {
  const { element, forId, value } = props;

  const entry = (
    <div style="margin-left:12px; margin-right:30px">
      <DescriptionEntry element={element} forId={forId} value={value} />
    </div>
  );

  return entry;
}

function Open(props) {
  const entry = (
    <button
      class="button-publish-properties"
      onClick={openProcessModel}
      title="Open BPMN diagram from local file"
    >
      <span class={"icon-open"}></span>
    </button>
  );

  return entry;
}

function Download(props) {
  const entry = (
    <button
      class="button-publish-properties"
      onClick={downloadProcessModel}
      title="Download as BPMN 2.0 file"
    >
      <span class={"icon-download"}></span>
    </button>
  );

  return entry;
}

function PublishModel(props) {
  const className = publishEnabled ? "icon-publish" : "icon-publish-disabled";

  const entry = (
    <button
      class="button-publish-properties"
      onClick={publishProcessModel}
      title="Publish model to Brixx-Process-Engine"
    >
      <span class={className}></span>
    </button>
  );

  return entry;
}

PublishPropertiesProvider.$inject = ["propertiesPanel", "translate", "moddle"];
